import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  applyNodeChanges,
  type NodeChange,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { toast } from 'sonner';
import { ArrowLeft, Plus, RefreshCw, Save } from 'lucide-react';
import { usePassbook } from '@/shared/lib/passbook';
import { useTheme } from '@/shared/lib/theme';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Spinner } from '@/shared/ui/spinner';
import { isSegmentationStale } from '../model/segmentation-status';
import {
  createSegmentation,
  fetchSegmentation,
  fetchSegmentationFieldCatalog,
  refreshSegmentationAudience,
  updateSegmentation,
} from '../api/use-segmentations';
import { useSegmentationCanvasStore } from '../model/segmentation-canvas-store';
import { parseSegmentationChannel, segmentationChannel } from '../model/segmentation-channel';
import { SegmentationFieldCatalogProvider } from '../model/segmentation-field-catalog-context';
import {
  buildCriteria,
  groupsToEdges,
  nodesToPositions,
  reconcileGroupNodes,
  type GroupNode,
} from '../model/segmentation-mapper';
import { SegmentationGroupNode } from './segmentation-group-node';
import { SegmentationConnectorEdge } from './segmentation-connector-edge';
import { SegmentationAudiencePreview } from './segmentation-audience-preview';
import { SegmentationChannelPicker } from './segmentation-channel-picker';
import { SegmentationSampleDrawer } from './segmentation-sample-drawer';
import { SegmentationChatWidget } from './segmentation-chat-widget';
import { SegmentationSaveDialog } from './segmentation-save-dialog';
import { SegmentationAiCostBadge } from './segmentation-ai-cost-badge';

const nodeTypes = { group: SegmentationGroupNode };
const edgeTypes = { connector: SegmentationConnectorEdge };

export function SegmentationBuilderPage() {
  const { idPassbook } = usePassbook();
  const { resolvedTheme } = useTheme();
  const { segmentationId } = useParams<{ segmentationId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditing = Boolean(segmentationId);

  const [nome, setNome] = useState('');
  const [saving, setSaving] = useState(false);
  /** Qual botão pediu o save — null = dialog de título fechado. */
  const [saveIntent, setSaveIntent] = useState<'stay' | 'exit' | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [sampleOpen, setSampleOpen] = useState(false);
  const [nodes, setNodes] = useState<GroupNode[]>([]);
  /** Uma conversa por abertura do construtor; no save liga o custo de IA à segmentação. */
  const [chatSessionId] = useState(() => crypto.randomUUID());
  const queryClient = useQueryClient();

  const groups = useSegmentationCanvasStore((s) => s.groups);
  const connectors = useSegmentationCanvasStore((s) => s.connectors);
  const positions = useSegmentationCanvasStore((s) => s.positions);
  const channel = useSegmentationCanvasStore((s) => s.channel);
  const setChannel = useSegmentationCanvasStore((s) => s.setChannel);
  const loadCriteria = useSegmentationCanvasStore((s) => s.loadCriteria);
  const addGroup = useSegmentationCanvasStore((s) => s.addGroup);
  const setNodePosition = useSegmentationCanvasStore((s) => s.setNodePosition);
  const reset = useSegmentationCanvasStore((s) => s.reset);

  const fieldCatalogQuery = useQuery({
    queryKey: ['segmentation-field-catalog', idPassbook],
    queryFn: () => fetchSegmentationFieldCatalog(idPassbook),
    staleTime: 5 * 60_000,
  });

  const segmentationQuery = useQuery({
    queryKey: ['segmentation', segmentationId],
    queryFn: () => fetchSegmentation(segmentationId as string, idPassbook),
    enabled: isEditing,
  });

  useEffect(() => {
    if (isEditing && segmentationQuery.data) {
      setNome(segmentationQuery.data.nome);
      loadCriteria(segmentationQuery.data.criteria, segmentationQuery.data.layout);
      useSegmentationCanvasStore.setState({ channel: segmentationChannel(segmentationQuery.data) });
    } else if (!isEditing) {
      reset();
      setNome('Nova segmentação');
      addGroup();
      // Vem do wizard de e-mail/SMS ("Criar segmentação"): já abre no canal da campanha.
      useSegmentationCanvasStore.setState({ channel: parseSegmentationChannel(searchParams.get('canal')) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, segmentationQuery.data]);

  const criteria = useMemo(() => buildCriteria(groups, connectors), [groups, connectors]);
  const edges = useMemo(() => groupsToEdges(groups, connectors), [groups, connectors]);

  useEffect(() => {
    setNodes((prev) => reconcileGroupNodes(groups, positions, prev));
  }, [groups, positions]);

  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      setNodes((nds) => applyNodeChanges(changes, nds) as GroupNode[]);
      for (const change of changes) {
        if (change.type === 'position' && change.position && change.dragging === false) {
          setNodePosition(change.id, change.position);
        }
      }
    },
    [setNodePosition],
  );

  /** Só chamado pelo dialog de título — `nomeFinal` é o nome confirmado lá. */
  async function handleSave(exitAfter: boolean, nomeFinal: string) {
    setSaving(true);
    try {
      const layout = { positions: nodesToPositions(nodes) };
      setNome(nomeFinal);
      if (isEditing && segmentationId) {
        const updated = await updateSegmentation(segmentationId, idPassbook, {
          nome: nomeFinal,
          channel,
          criteria,
          layout,
          aiChatSessionId: chatSessionId,
        });
        queryClient.setQueryData(['segmentation', segmentationId], updated);
        toast.success('Segmentação salva.');
      } else {
        const created = await createSegmentation({
          id_passbook: idPassbook,
          nome: nomeFinal,
          channel,
          criteria,
          layout,
          aiChatSessionId: chatSessionId,
        });
        toast.success('Segmentação criada.');
        if (!exitAfter) {
          setSaveIntent(null);
          navigate(`../${created._id}/editar`, { replace: true });
          return;
        }
      }
      setSaveIntent(null);
      if (exitAfter) navigate('..');
    } catch (err) {
      // Mantém o dialog aberto: o usuário corrige o nome (ou só tenta de novo).
      toast.error(err instanceof Error ? err.message : 'Não foi possível salvar a segmentação.');
    } finally {
      setSaving(false);
    }
  }

  async function handleRefreshAudience() {
    if (!segmentationId) {
      toast.info('Salve a segmentação antes de atualizar o público-alvo.');
      return;
    }
    setRefreshing(true);
    try {
      const updated = await refreshSegmentationAudience(segmentationId, idPassbook);
      toast.success(`Público-alvo atualizado: ${updated.leadCount ?? 0} leads.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível atualizar o público-alvo.');
    } finally {
      setRefreshing(false);
    }
  }

  const stale = isEditing && segmentationQuery.data ? isSegmentationStale(segmentationQuery.data) : false;

  if (isEditing && segmentationQuery.isLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <SegmentationFieldCatalogProvider value={fieldCatalogQuery.data ?? []}>
      <div className="flex h-full min-h-0 flex-1 flex-col">
        <header className="flex shrink-0 flex-col gap-2 border-b border-border/60 px-3 py-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3 sm:px-6 sm:py-3">
          <div className="flex min-w-0 items-center gap-2">
            <Button variant="ghost" size="icon-sm" onClick={() => navigate('..')} aria-label="Voltar">
              <ArrowLeft className="size-4" />
            </Button>
            <Input
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              className="h-9 min-w-0 flex-1 border-transparent bg-transparent px-2 text-base font-semibold shadow-none hover:border-input focus-visible:border-input sm:max-w-xs sm:flex-none sm:w-64"
            />
            <SegmentationAudiencePreview idPassbook={idPassbook} criteria={criteria} channel={channel} />
            <SegmentationAiCostBadge usage={segmentationQuery.data?.aiUsage} />
            {stale ? (
              <span className="shrink-0 rounded-full bg-warning/10 px-2 py-1 text-[10px] font-medium text-warning sm:px-2.5 sm:text-xs">
                <span className="sm:hidden">Desatualizado</span>
                <span className="hidden sm:inline">Público desatualizado</span>
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-2 pl-10 sm:pl-0">
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => setSampleOpen(true)}
            >
              <span className="hidden md:inline">Ver amostra de leads</span>
              <span className="md:hidden">Amostra</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={handleRefreshAudience}
              loading={refreshing}
              aria-label="Atualizar público-alvo"
            >
              <RefreshCw className="size-3.5" />
              <span className="hidden md:inline">Atualizar público-alvo</span>
              <span className="md:hidden">Atualizar</span>
            </Button>
            <Button variant="secondary" size="sm" className="flex-1 sm:flex-none" onClick={() => setSaveIntent('stay')} loading={saving}>
              Salvar
            </Button>
            <Button variant="primary" size="sm" className="flex-1 sm:flex-none" onClick={() => setSaveIntent('exit')} loading={saving}>
              <Save className="size-3.5" />
              <span className="hidden sm:inline">Salvar e sair</span>
              <span className="sm:hidden">Sair</span>
            </Button>
          </div>
        </header>

        <div className="relative flex min-h-0 flex-1 overflow-hidden">
          <div className="relative h-full min-h-0 w-full flex-1">
            <ReactFlow
              style={{ width: '100%', height: '100%' }}
              colorMode={resolvedTheme}
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              onNodesChange={handleNodesChange}
              fitView
              fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
              minZoom={0.2}
              maxZoom={1.5}
              nodesConnectable={false}
              elementsSelectable
              proOptions={{ hideAttribution: true }}
            >
              <Background gap={24} />
              <Controls showInteractive={false} position="top-left" className="!m-2 lg:!m-4" />
              <MiniMap pannable zoomable className="!hidden !bg-card lg:!block" />
            </ReactFlow>

            <div className="pointer-events-auto absolute left-1/2 top-3 z-10 -translate-x-1/2 lg:top-4">
              <SegmentationChannelPicker value={channel} onChange={setChannel} />
            </div>

            <div className="absolute bottom-[5.5rem] left-3 z-10 sm:bottom-6 sm:left-1/2 sm:-translate-x-1/2">
              <Button variant="secondary" className="shadow-glass-lg" onClick={addGroup}>
                <Plus className="size-4" />
                <span className="sm:hidden">Grupo</span>
                <span className="hidden sm:inline">Adicionar novo grupo</span>
              </Button>
            </div>
          </div>
        </div>

        <SegmentationSampleDrawer
          open={sampleOpen}
          onOpenChange={setSampleOpen}
          idPassbook={idPassbook}
          criteria={criteria}
          channel={channel}
        />

        <SegmentationSaveDialog
          open={saveIntent !== null}
          onOpenChange={(next) => {
            if (!next) setSaveIntent(null);
          }}
          nome={nome}
          confirmLabel={saveIntent === 'exit' ? 'Salvar e sair' : 'Salvar'}
          saving={saving}
          onConfirm={(nomeFinal) => void handleSave(saveIntent === 'exit', nomeFinal)}
        />

        <SegmentationChatWidget
          idPassbook={idPassbook}
          segmentationId={segmentationId}
          chatSessionId={chatSessionId}
          onSegmentationReady={() => setSaveIntent('stay')}
        />
      </div>
    </SegmentationFieldCatalogProvider>
  );
}
